import { APP_SLUG } from "../../constants/index.ts";
import { getInjectedConfig } from "./get-injected-config.ts";

/**
 * Returns the current app slug, considering (in order):
 *
 * 1. The injected `window.__APP_CONFIG.app.slug` (preferred — it is the
 *    server's authoritative answer for this request, and fan-api treats
 *    domain alias hosts as single-tenant).
 * 2. The `APP_SLUG` value in local storage (carries the slug between
 *    pre-config bootstraps).
 * 3. The `?app=` query param (legacy entry).
 * 4. The pathname (matches `/oidc/:slug` or `/demo/:slug`).
 * 5. The first part of the hostname (last-resort heuristic for dev).
 *
 * If injected config is present and disagrees with local storage, local
 * storage is replaced so subsequent requests stop sending the stale slug.
 */
export function getCurrentApp() {
  const injectedSlug = getInjectedConfig()?.app.slug;

  if (injectedSlug) {
    if (localStorage.getItem(APP_SLUG) !== injectedSlug) {
      setAppInLocalStorage(injectedSlug);
    }
    return injectedSlug;
  }

  const appFromLocalStorage = localStorage.getItem(APP_SLUG);

  if (appFromLocalStorage) {
    return appFromLocalStorage;
  }

  const app =
    getAppFromQueryParam() ?? getAppFromPathname() ?? getAppFromSubdomain();

  if (app) {
    setAppInLocalStorage(app);
  }

  return app;
}

function getAppFromPathname() {
  const oidcMatch = window.location.pathname.match(/\/oidc\/(.*)/);

  if (oidcMatch) {
    return oidcMatch[1];
  }

  const demoMatch = window.location.pathname.match(/\/demo\/(.*)/);

  if (demoMatch) {
    return demoMatch[1];
  }
}

function getAppFromSubdomain() {
  // This regex matches the first part of the hostname, e.g. "nfl-events". Note
  // that it will match the first part of any hostname, e.g., `bingo.test` will
  // match `bingo`, which is incorrect. Injected config is preferred over this
  // heuristic; this is only the dev fallback before the page-level config is
  // available.
  const subdomainMatch = window.location.hostname.match(/^([^.]+)\./);

  if (subdomainMatch) {
    return subdomainMatch[1];
  }
}

function getAppFromQueryParam() {
  return new URLSearchParams(location.search).get("app");
}

function setAppInLocalStorage(app: string) {
  localStorage.setItem(APP_SLUG, app);
}
